import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import AshdaleLogoSVG from '../assets/ashdale-logo.svg';

const { FiTrendingUp, FiHome, FiBookOpen, FiBarChart3, FiShield, FiUsers, FiArrowRight, FiCheckCircle } = FiIcons;

const Home = () => {
  const calculators = [
    {
      title: 'Retirement Planning',
      description: 'Plan for a secure retirement with our comprehensive tools',
      icon: FiTrendingUp,
      link: '/calculators/retirement',
      color: 'bg-primary-500'
    },
    {
      title: 'Home Purchase',
      description: 'Calculate mortgage payments and home affordability',
      icon: FiHome,
      link: '/calculators/home',
      color: 'bg-primary-500'
    },
    {
      title: 'Education Planning',
      description: 'Plan for education expenses with confidence',
      icon: FiBookOpen,
      link: '/calculators/education',
      color: 'bg-primary-500'
    }
  ];

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-32">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-500/10 to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <img src={AshdaleLogoSVG} alt="Ashdale Analytics" className="h-24 mx-auto mb-10" />
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-8">
              Data-Driven <span className="text-primary-500">Financial Planning</span>
            </h1>
            <p className="text-xl md:text-2xl text-secondary-300 max-w-3xl mx-auto mb-12 leading-relaxed">
              Empowering individuals with professional-grade analytics to navigate retirement, home ownership, and education funding.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/register"
                className="bg-primary-500 text-black px-10 py-4 rounded-xl font-bold text-lg hover:bg-primary-400 transition-all shadow-2xl shadow-primary-500/20 active:scale-95 flex items-center justify-center"
              >
                Get Started Free <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/calculators/retirement"
                className="bg-secondary-900 text-white border border-secondary-700 px-10 py-4 rounded-xl font-bold text-lg hover:bg-secondary-800 transition-all active:scale-95"
              >
                View Calculators
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Calculators Section */}
      <section className="py-24 bg-secondary-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">Our Solutions</h2>
            <div className="w-20 h-1.5 bg-primary-500 mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {calculators.map((calc, index) => (
              <motion.div
                key={calc.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-secondary-900 p-8 rounded-3xl border border-primary-500/10 hover:border-primary-500/30 transition-all group"
              >
                <div className="w-16 h-16 bg-primary-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary-500 transition-colors">
                  <SafeIcon icon={calc.icon} className="w-8 h-8 text-primary-500 group-hover:text-black transition-colors" />
                </div>
                <h3 className="text-2xl font-bold mb-4">{calc.title}</h3>
                <p className="text-secondary-400 mb-8 leading-relaxed">{calc.description}</p>
                <Link to={calc.link} className="flex items-center text-primary-500 font-bold group-hover:translate-x-2 transition-transform">
                  Try Calculator <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight">
                Why Thousands Trust <br />
                <span className="text-primary-500">Ashdale Analytics</span>
              </h2>
              <div className="space-y-6">
                {[
                  'Bank-level data security and encryption',
                  'Real-time market-adjusted projections',
                  'Comprehensive multi-scenario modeling',
                  'Expert-vetted financial algorithms'
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-primary-500 flex-shrink-0" />
                    <span className="text-lg text-secondary-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-secondary-900 to-black p-12 rounded-3xl border border-primary-500/20 shadow-2xl">
              <div className="flex items-center space-x-4 mb-8">
                <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiBarChart3} className="text-black w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold">Analytics Engine</h3>
              </div>
              <p className="text-secondary-400 text-lg leading-relaxed mb-8">
                Our proprietary engine processes thousands of data points to give you the most accurate financial forecasting available to retail consumers.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-black/50 p-4 rounded-xl border border-secondary-800">
                  <p className="text-primary-500 text-3xl font-bold mb-1">99.9%</p>
                  <p className="text-secondary-500 text-sm">Accuracy Rate</p>
                </div>
                <div className="bg-black/50 p-4 rounded-xl border border-secondary-800">
                  <p className="text-primary-500 text-3xl font-bold mb-1">50k+</p>
                  <p className="text-secondary-500 text-sm">Active Plans</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;